import { useState } from 'react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/useAuth';
import { FormNotice } from './MemberForm';
import { messageFrom } from './memberErrors';

/**
 * Asks an unconfirmed member to confirm their email, with a way to send the link
 * again.
 *
 * Without this, the only way to get a second link was an admin: the first one
 * expires after two days, and a link that lands in spam is as good as lost. The
 * API limits it to one a minute, and the message it sends back when asked too
 * soon is shown as it comes.
 */
export function MemberConfirmNotice() {
  const { member, refreshMember } = useAuth();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!member || member.verified) return null;

  const resend = async () => {
    setError(null);
    setBusy(true);
    try {
      const result = await api.member.resendConfirmation();
      if (result.alreadyConfirmed) {
        // Confirmed in another tab, or by us. Re-reading makes this go away.
        await refreshMember();
        return;
      }
      if (result.sent) setSent(true);
      else setError('We could not send the email just now. Please try again in a few minutes.');
    } catch (err) {
      setError(messageFrom(err, 'We could not send the email. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="member-confirm">
      <FormNotice kind="info">
        {sent
          ? `A new confirmation link is on its way to ${member.email}. It works for 48 hours.`
          : 'Your email address is not confirmed yet. Use the link we emailed you when you signed up.'}
      </FormNotice>
      {error ? <FormNotice kind="error">{error}</FormNotice> : null}
      {!sent && (
        <button
          type="button"
          className="button button-default-outline button-block"
          onClick={() => void resend()}
          disabled={busy}
        >
          {busy ? 'Sending…' : 'Send a new confirmation link'}
        </button>
      )}
    </div>
  );
}
